import { useAuth0 } from "@auth0/auth0-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useShallow } from "zustand/shallow";
import { setAuth0Token } from "../../auth/token";
import { GoogleOAuth2Provider, WindowsLiveProvider } from "../../constants";
import { useLoginFromAuth0Mutation } from "../../graphql/mutation.loginFromAuth0";
import { useChartsCountQuery } from "../../graphql/query.chartsCount";
import {
  MessageFromAddInType,
  subscribeToMessageType,
} from "../../helpers/window-events";
import { useStore } from "../../store/store";
import { User } from "../../types";
import { AuthContext, UpdatableUserFields } from "./context";

interface AuthContextProviderProps {
  children: React.ReactNode;
}

// 'a' is the token that is passed in the URL when the user opens the app from the add-in.
const queryParams = new URLSearchParams(location.search);
let urlToken = queryParams.get("a");
if (urlToken && !urlToken.trim().length) {
  urlToken = null;
}

// Remove the token from the URL if it exists
if (urlToken && urlToken.length > 0) {
  queryParams.delete("a");
  window.history.replaceState(
    {},
    "",
    `${location.pathname}${
      queryParams.toString() ? "?" + queryParams.toString() : ""
    }`
  );
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const {
    getAccessTokenSilently,
    isAuthenticated: isAuth0Authenticated,
    logout: auth0Logout,
  } = useAuth0();

  const accessTokenRef = useRef<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticatedFromUrl, setIsAuthenticatedFromUrl] = useState(false);
  const { setChartsCount, setUser, user } = useStore(
    useShallow((state) => ({
      setChartsCount: state.setChartsCount,
      setUser: state.setUser,
      user: state.user,
    }))
  );

  const [runLogin, { data: loginData, error: loginError }] =
    useLoginFromAuth0Mutation();

  const runChartsCountQuery = useChartsCountQuery({
    skip: true,
  });

  useEffect(() => {
    let isMounted = true;

    // if the urlToken is present in the URL, we set it as the accessToken and we call the login mutation directly
    // so we bypass the Auth0 login flow
    // this is useful when the user opens the app from the add-in and the token is passed in the URL
    if (urlToken) {
      accessTokenRef.current = urlToken;
      void runLogin({ variables: { accessToken: urlToken } });
      return;
    }

    const loginToJane = async () => {
      if (!isMounted) {
        return;
      }
      try {
        const accessToken = await getAccessTokenSilently();
        accessTokenRef.current = accessToken;
        void runLogin({ variables: { accessToken } });
      } catch (error) {
        console.error("Error getting access token:", error);
        logout();
        return;
      }
    };

    if (isAuth0Authenticated) {
      void loginToJane();
    }

    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, isAuth0Authenticated, runLogin]);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(undefined);
    void auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout]);

  useEffect(() => {
    if (loginError) {
      logout();
    }

    if (loginData && accessTokenRef.current) {
      // we set the token after the login, because it will be used in the apollo client for further requests
      setAuth0Token(accessTokenRef.current);
      setIsAuthenticated(true);
      if (urlToken) {
        setIsAuthenticatedFromUrl(true);
      }
      let socialProvider: User["socialProvider"] = undefined;
      if (loginData.loginFromAuth0.id.startsWith(WindowsLiveProvider)) {
        socialProvider = WindowsLiveProvider;
      }
      if (loginData.loginFromAuth0.id.startsWith(GoogleOAuth2Provider)) {
        socialProvider = GoogleOAuth2Provider;
      }
      setUser({
        email: loginData.loginFromAuth0.email,
        firstName: loginData.loginFromAuth0.firstName,
        lastName: loginData.loginFromAuth0.lastName,
        name: loginData.loginFromAuth0.name,
        picture: loginData.loginFromAuth0.picture,
        subscriptionId: loginData.loginFromAuth0.subscriptionId,
        subscriptionExpiresAt: loginData.loginFromAuth0.subscriptionExpiresAt,
        socialProvider,
      });
      setChartsCount(loginData.loginFromAuth0.chartsCount);
      // jane login OK
    }
  }, [loginData, loginError, logout]);

  const updateUser = (userData: UpdatableUserFields) => {
    if (!user) {
      return;
    }
    const updatedUser: User = {
      ...user,
      ...userData,
    };
    setUser(updatedUser);
  };

  const refreshChartsCount = useCallback(async () => {
    try {
      const response = await runChartsCountQuery.refetch();
      if (response.data) {
        setChartsCount(response.data.chartsCount.chartsCount);
      }
    } catch (error) {
      console.error("Error fetching charts count:", error);
    }
  }, [runChartsCountQuery]);

  // Fetch charts count when the add-in send the chart created message
  useEffect(() => {
    // Subscribe to the message from the add-in for token expiration
    const unsubscribe = subscribeToMessageType(
      MessageFromAddInType.CHART_CREATED,
      refreshChartsCount
    );
    return () => {
      unsubscribe();
    };
  }, [refreshChartsCount]);

  const hasSubscription = useMemo(() => {
    if (!user) {
      return false;
    }
    return (
      user.subscriptionId !== null &&
      user.subscriptionExpiresAt !== null &&
      new Date(user.subscriptionExpiresAt!) > new Date()
    );
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthenticatedFromUrl,
        authToken: accessTokenRef.current,
        logout,
        user,
        updateUser,
        hasSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
